/**
 * TaskAgentOutput — TASK-702.
 *
 * What the agent produced, and what it did to produce it. Before this the review card showed a
 * title, a priority pill, "prepared 2 hours ago" and a ✓ — so **signing off was a rubber stamp
 * on unread work, on the one surface whose entire purpose is being the human gate**.
 *
 * The endpoint has returned `{task, events, artifacts}` since TASK-684; nothing rendered it.
 *
 * Honesty rules:
 *  - an artifact that cannot be opened says so, and never renders as a dead link;
 *  - derived `trail_gaps` are shown — an incomplete audit trail is exactly what a reviewer
 *    needs to know BEFORE signing;
 *  - the agent's approach and attempt summary are attributed to the actor, so the RM can see
 *    that an agent did this and what it thought it was doing.
 */
import { AlertTriangle, Bot, ExternalLink, FileWarning, User } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { TaskArtifact, TaskDetailResponse, TaskEvent } from '../../types/tasks';
import { resolveArtifact } from './taskArtifactResolver';

interface TaskAgentOutputProps {
  detail: TaskDetailResponse;
  /** Called when the RM opens an artifact — the review gate records that it was read. */
  onArtifactOpened?: (artifact: TaskArtifact) => void;
  /**
   * Called when the RM accepts that an artifact cannot be opened. The gate stays closed until
   * they do: unreadable work is still unread work, and signing it off must be a decision.
   */
  onArtifactAcknowledged?: (artifact: TaskArtifact) => void;
  /** Artifacts already acknowledged in this review, so the control can report the state back. */
  acknowledged?: ReadonlySet<string>;
}

const isAgent = (e: TaskEvent) => e.actor_kind === 'agent';

/** Newest-first is how the endpoint returns events; find the newest of a kind. */
const newest = (events: TaskEvent[], kind: string) =>
  events.find((e) => e.event === kind);

export function TaskAgentOutput({
  detail,
  onArtifactOpened,
  onArtifactAcknowledged,
  acknowledged,
}: TaskAgentOutputProps) {
  const { events, artifacts, trail_gaps: gaps, blocked_by: blockedBy } = detail;
  const claimed = newest(events, 'claimed');
  const attempted = newest(events, 'attempted');
  const approach = claimed?.detail?.approach || attempted?.detail?.approach;
  const summary = attempted?.detail?.summary;
  const worked = attempted ?? claimed;
  const actor = worked?.actor;
  const agentDid = Boolean(worked && isAgent(worked));

  return (
    <div className="space-y-3" data-testid="task-agent-output">
      {/* What it produced */}
      {artifacts.length > 0 ? (
        <ul className="space-y-1.5" aria-label="What the agent produced">
          {artifacts.map((artifact) => {
            const target = resolveArtifact(artifact);
            return (
              <li key={artifact.id} data-testid="task-artifact">
                {target.kind === 'route' ? (
                  <Link
                    to={target.to}
                    onClick={() => onArtifactOpened?.(artifact)}
                    data-testid="task-artifact-open"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-kielis-navy hover:underline"
                  >
                    <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                    Open the {target.label.toLowerCase()}
                  </Link>
                ) : (
                  // Never a dead link. The reviewer is told the work exists and why they
                  // cannot read it — which is a different, and much safer, message than a
                  // blank page.
                  <div
                    data-testid="task-artifact-unresolvable"
                    className="rounded border border-amber-200 bg-amber-50 p-2 text-sm text-amber-900"
                  >
                    <div className="flex items-start gap-1.5">
                      <FileWarning
                        className="mt-0.5 h-3.5 w-3.5 flex-shrink-0"
                        aria-hidden="true"
                      />
                      <span>
                        <strong>{target.label}</strong> — {target.reason}
                      </span>
                    </div>
                    {/* The escape hatch is necessary but not free: signing off on work nobody
                        could read is a decision, so it takes a click of its own. */}
                    {onArtifactAcknowledged &&
                      (acknowledged?.has(artifact.id) ? (
                        <p className="mt-1 pl-5 text-[11px]" data-testid="task-artifact-acknowledged">
                          You have accepted that this cannot be opened.
                        </p>
                      ) : (
                        <button
                          type="button"
                          onClick={() => onArtifactAcknowledged(artifact)}
                          data-testid="task-artifact-acknowledge"
                          className="mt-1 ml-5 rounded border border-amber-300 px-1.5 py-0.5 text-[11px] font-medium hover:bg-amber-100"
                        >
                          I accept that this cannot be opened
                        </button>
                      ))}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        // No artifact was declared, so the attempt summary IS the output the RM reviews.
        <p className="text-sm text-muted-foreground" data-testid="task-no-artifact">
          The agent attached no artifact to this task.
        </p>
      )}

      {/* What it did */}
      {(approach || summary) && (
        <div className="rounded border border-border bg-muted/30 p-2" data-testid="task-agent-trail">
          <p className="mb-1 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {agentDid ? (
              <Bot className="h-3 w-3" aria-hidden="true" />
            ) : (
              <User className="h-3 w-3" aria-hidden="true" />
            )}
            {agentDid ? 'Done by the AI' : 'Done by'} {actor}
          </p>
          {approach && (
            <p className="text-sm text-foreground">
              <span className="text-muted-foreground">Approach: </span>
              {approach}
            </p>
          )}
          {summary && (
            <p className="text-sm text-foreground">
              <span className="text-muted-foreground">Result: </span>
              {summary}
            </p>
          )}
        </div>
      )}

      {/* Why it stopped, when it did */}
      {blockedBy && (
        <div
          data-testid="task-blocked-reason"
          className="flex items-start gap-1.5 rounded border border-orange-200 bg-orange-50 p-2 text-sm text-orange-900"
        >
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
          <span>
            <strong>Blocked:</strong> {blockedBy}
          </span>
        </div>
      )}

      {/* Holes in the trail — a reviewer must know before signing */}
      {gaps.length > 0 && (
        <div
          data-testid="task-trail-gaps"
          className="rounded border border-amber-200 bg-amber-50 p-2 text-sm text-amber-900"
        >
          <p className="flex items-center gap-1.5 font-medium">
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
            This task&apos;s audit trail is incomplete
          </p>
          <ul className="ml-5 list-disc">
            {gaps.map((gap) => (
              <li key={gap}>{gap}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default TaskAgentOutput;
